/**
 * Theory of Sovereign Reflectivity Visualization
 * Main JavaScript file
 */

import p5 from 'p5';
import { setupHeroAnimation } from './hero-animation.js';

// Determine the current page type
const isVisualizationPage = window.location.pathname.includes('/visualizations/');
const isTheoryPage = window.location.pathname.includes('/theory/');
const isHomePage = !isVisualizationPage && !isTheoryPage;

// We no longer need to dynamically import theory-animation.js for theory pages
// as it's now included directly in the HTML with a script tag

// Only import visualization modules for visualization pages
if (isVisualizationPage) {
    // Determine which visualization to load based on the current page
    const currentPath = window.location.pathname;

    if (currentPath.includes('energy-equivalence')) {
        import('./visualizations/energy-equivalence.js').catch(err => {
            console.error('Failed to load energy-equivalence visualization:', err);
        });
    } else if (currentPath.includes('vibrational-lensing')) {
        import('./visualizations/vibrational-lensing.js').catch(err => {
            console.error('Failed to load vibrational-lensing visualization:', err);
        });
    } else if (currentPath.includes('resistance-filter')) {
        import('./visualizations/resistance-filter.js').catch(err => {
            console.error('Failed to load resistance-filter visualization:', err);
        });
    } else if (currentPath.includes('consciousness-kinetic')) {
        import('./visualizations/consciousness-kinetic.js').catch(err => {
            console.error('Failed to load consciousness-kinetic visualization:', err);
        });
    } else if (currentPath.includes('time-dilation')) {
        import('./visualizations/time-dilation.js').catch(err => {
            console.error('Failed to load time-dilation visualization:', err);
        });
    } else if (currentPath.includes('length-contraction')) {
        import('./visualizations/length-contraction.js').catch(err => {
            console.error('Failed to load length-contraction visualization:', err);
        });
    }
}

// Wait for DOM to be fully loaded
document.addEventListener('DOMContentLoaded', () => {
    try {
        // Initialize the hero animation on the home page
        const heroCanvas = document.getElementById('hero-canvas');
        if (heroCanvas) {
            try {
                new p5(setupHeroAnimation, 'hero-canvas');
            } catch (err) {
                console.warn('Failed to initialize hero animation:', err);
            }
        }

        // Remove any existing menu overlay that might have persisted from previous page
        const existingOverlay = document.querySelector('.menu-overlay');
        if (existingOverlay) {
            existingOverlay.remove();
        }

        // Hamburger menu functionality with dynamic effects
        const hamburgerMenu = document.querySelector('.hamburger-menu');
        const mainNav = document.querySelector('.main-nav');

        // Create menu overlay - this line is now removed as menuOverlay is created inside initHamburgerMenu

        // Initialize hamburger menu functionality
        function initHamburgerMenu() {
            const hamburgerMenu = document.querySelector('.hamburger-menu');
            const mainNav = document.querySelector('.main-nav');
            let menuParticlesInitialized = false;
            let menuParticlesCanvas = null;

            // Create menu overlay
            const menuOverlay = document.createElement('div');
            menuOverlay.classList.add('menu-overlay');
            document.body.appendChild(menuOverlay);

            if (hamburgerMenu && mainNav) {
                console.log('Hamburger menu initialized');

                // Add click handlers to all menu links to ensure overlay is removed on navigation
                const menuLinks = mainNav.querySelectorAll('a');
                menuLinks.forEach(link => {
                    link.addEventListener('click', () => {
                        // Close menu before navigating
                        hamburgerMenu.classList.remove('active');
                        mainNav.classList.remove('active');
                        menuOverlay.classList.remove('active');
                        document.body.classList.remove('menu-open');
                    });
                });

                // Toggle menu when clicking hamburger icon
                hamburgerMenu.addEventListener('click', () => {
                    console.log('Hamburger clicked');
                    hamburgerMenu.classList.toggle('active');
                    mainNav.classList.toggle('active');
                    menuOverlay.classList.toggle('active');

                    if (mainNav.classList.contains('active')) {
                        document.body.style.overflow = 'hidden';

                        // Add particle effects to menu
                        if (!menuParticlesInitialized) {
                            menuParticlesCanvas = initMenuParticles();
                            menuParticlesInitialized = true;
                        } else if (menuParticlesCanvas) {
                            menuParticlesCanvas.style.display = 'block';
                        }
                    } else {
                        document.body.style.overflow = '';
                        if (menuParticlesCanvas) {
                            menuParticlesCanvas.style.display = 'none';
                        }
                    }
                });
            }
        }

        initHamburgerMenu();


        // Add particle effects to the menu
        function initMenuParticles() {
            // Remove any existing particles
            const existingContainer = document.getElementById('menu-particles');
            if (existingContainer) {
                existingContainer.remove();
            }

            const particleContainer = document.createElement('div');
            particleContainer.id = 'menu-particles';
            particleContainer.style.position = 'absolute';
            particleContainer.style.top = '0';
            particleContainer.style.left = '0';
            particleContainer.style.width = '100%';
            particleContainer.style.height = '100%';
            particleContainer.style.pointerEvents = 'none';
            particleContainer.style.zIndex = '1';

            document.querySelector('.main-nav').appendChild(particleContainer);

            // Use p5.js to create particles if p5 is available
            if (typeof p5 !== 'undefined') {
                new p5((p) => {
                    console.log('Initializing menu particles');
                    const particles = [];
                    const particleCount = 50;
                    const connectionDistance = 100;

                    p.setup = function() {
                        const canvas = p.createCanvas(window.innerWidth, window.innerHeight);
                        canvas.parent('menu-particles');

                        // Colors from the main site theme
                        const colors = [
                            p.color(241, 196, 15, 200),  // Gold accent
                            p.color(142, 68, 173, 150),  // Purple secondary
                            p.color(44, 62, 80, 100)     // Deep indigo primary
                        ];

                        for (let i = 0; i < particleCount; i++) {
                            particles.push({
                                x: p.random(p.width),
                                y: p.random(p.height),
                                size: p.random(2, 7),
                                speedX: p.random(-0.8, 0.8),
                                speedY: p.random(-0.8, 0.8),
                                color: colors[Math.floor(p.random(3))],
                                pulseSpeed: p.random(0.01, 0.05),
                                pulseAngle: p.random(p.TWO_PI)
                            });
                        }
                    };

                    p.draw = function() {
                        p.clear();

                        // First draw connections between particles
                        p.stroke(241, 196, 15, 50); // Light gold for connections
                        p.strokeWeight(0.5);

                        for (let i = 0; i < particles.length; i++) {
                            for (let j = i + 1; j < particles.length; j++) {
                                const p1 = particles[i];
                                const p2 = particles[j];
                                const d = p.dist(p1.x, p1.y, p2.x, p2.y);

                                if (d < connectionDistance) {
                                    const alpha = p.map(d, 0, connectionDistance, 100, 0);
                                    p.stroke(241, 196, 15, alpha);
                                    p.line(p1.x, p1.y, p2.x, p2.y);
                                }
                            }
                        }

                        // Then draw particles
                        for (let i = 0; i < particles.length; i++) {
                            const particle = particles[i];

                            // Update pulse
                            particle.pulseAngle += particle.pulseSpeed;
                            const pulseFactor = 0.5 + 0.5 * Math.sin(particle.pulseAngle);

                            // Update position
                            particle.x += particle.speedX;
                            particle.y += particle.speedY;

                            // Wrap around edges
                            if (particle.x < 0) particle.x = p.width;
                            if (particle.x > p.width) particle.x = 0;
                            if (particle.y < 0) particle.y = p.height;
                            if (particle.y > p.height) particle.y = 0;

                            // Draw particle
                            p.fill(particle.color);
                            p.noStroke();
                            const displaySize = particle.size * (0.75 + pulseFactor * 0.5);
                            p.ellipse(particle.x, particle.y, displaySize);
                        }
                    };

                    // Handle window resize
                    p.windowResized = function() {
                        p.resizeCanvas(window.innerWidth, window.innerHeight);
                    };
                });

                return particleContainer;
            }


            // Close menu when clicking on a link
            const navLinks = mainNav.querySelectorAll('a');
            navLinks.forEach(link => {
                link.addEventListener('click', () => {
                    hamburgerMenu.classList.remove('active');
                    mainNav.classList.remove('active');
                    menuOverlay.classList.remove('active');
                    document.body.style.overflow = '';
                });
            });

            // Close menu when clicking on overlay
            menuOverlay.addEventListener('click', () => {
                hamburgerMenu.classList.remove('active');
                mainNav.classList.remove('active');
                menuOverlay.classList.remove('active');
                document.body.style.overflow = '';
            });


            // Smooth scrolling for navigation links
            document.querySelectorAll('a[href^="#"]').forEach(anchor => {
                anchor.addEventListener('click', function (e) {
                    e.preventDefault();
                    const targetId = this.getAttribute('href');
                    const targetElement = document.querySelector(targetId);

                    if (targetElement) {
                        window.scrollTo({
                            top: targetElement.offsetTop - 80, // Offset for fixed header
                            behavior: 'smooth'
                        });
                    }
                });
            });

            // Add active class to navigation links based on scroll position
            const sections = document.querySelectorAll('section[id]');
            const navLinks2 = document.querySelectorAll('.main-nav a');

            function highlightNavLink() {
                const scrollPosition = window.scrollY + 100; // Offset for fixed header

                sections.forEach(section => {
                    const sectionTop = section.offsetTop;
                    const sectionHeight = section.offsetHeight;
                    const sectionId = section.getAttribute('id');

                    if (scrollPosition >= sectionTop && scrollPosition < sectionTop + sectionHeight) {
                        navLinks2.forEach(link => {
                            link.classList.remove('active');
                            if (link.getAttribute('href') === `#${sectionId}`) {
                                link.classList.add('active');
                            }
                        });
                    }
                });
            }

            // Initial call to highlight the correct nav link
            highlightNavLink();

            // Add scroll event listener
            window.addEventListener('scroll', highlightNavLink);

            // Add animation classes on scroll
            const animateElements = document.querySelectorAll('.animate-on-scroll');

            function checkIfInView() {
                const windowHeight = window.innerHeight;
                const windowTopPosition = window.scrollY;
                const windowBottomPosition = windowTopPosition + windowHeight;

                animateElements.forEach(element => {
                    const elementHeight = element.offsetHeight;
                    const elementTopPosition = element.offsetTop;
                    const elementBottomPosition = elementTopPosition + elementHeight;

                    // Check if element is in view
                    if (
                        (elementBottomPosition >= windowTopPosition) &&
                        (elementTopPosition <= windowBottomPosition)
                    ) {
                        element.classList.add('fade-in');
                    }
                });
            }

            // Initial check for elements in view
            checkIfInView();

            // Add scroll event listener for animations
            window.addEventListener('scroll', checkIfInView);

            // Add animation classes to visualization cards with delay
            const vizCards = document.querySelectorAll('.visualization-card');
            vizCards.forEach((card, index) => {
                setTimeout(() => {
                    card.classList.add('slide-up');
                }, 100 * index);
            });

            // Handle visualization card clicks
            document.querySelectorAll('.visualization-card').forEach(card => {
                card.addEventListener('click', function (e) {
                    // Only handle clicks on the card itself, not on the button
                    if (!e.target.closest('.btn')) {
                        const vizType = this.getAttribute('data-viz');
                        const link = this.querySelector('.btn');
                        if (link) {
                            const href = link.getAttribute('href');
                            if (href) {
                                window.location.href = href;
                            }
                        }
                    }
                });
            });
        }

    } catch (err) {
        console.error('Error initializing main module:', err);
    }
});